import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  FileText,
  Upload,
  CreditCard,
  CheckCircle2,
  XCircle,
  Award,
  ArrowLeft,
  ShieldCheck,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = ["transcript", "ID proof", "CE certificate", "license", "headshot", "other"];

export default function ApplicationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [appData, setAppData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [a, f] = await Promise.all([
        api.get(`/applications/${id}`),
        api.get(`/files`, { params: { application_id: id } }),
      ]);
      setAppData(a.data);
      setDocs(f.data);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !appData) return <div className="text-sm text-slate-500">Loading…</div>;

  const isOwner = appData.applicant_user_id === user.id;
  const isStaff = user.role === "admin" || user.role === "reviewer";

  return (
    <div className="animate-fade-up" data-testid="application-detail">
      <Link to="/dashboard/applications" className="inline-flex items-center text-sm text-slate-600 hover:text-primary mb-4">
        <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to applications
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="bg-slate-100 text-slate-700 font-mono-data">{appData.credential_type}</Badge>
            <Badge className="bg-primary text-white">{appData.organization}</Badge>
            <StatusBadge status={appData.status} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-primary">{appData.applicant_name}</h1>
          <div className="text-sm text-slate-500">{appData.applicant_email}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Application ID</div>
          <div className="font-mono-data text-xs text-primary">{appData.id.slice(0, 13)}…</div>
          <div className="text-xs text-slate-500 mt-1">Created {new Date(appData.created_at).toLocaleDateString()}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="bg-slate-100 p-1">
                <TabsTrigger value="overview" data-testid="detail-tab-overview"><ClipboardList className="h-3.5 w-3.5 mr-1.5" />Overview</TabsTrigger>
                <TabsTrigger value="docs" data-testid="detail-tab-docs"><FileText className="h-3.5 w-3.5 mr-1.5" />Documents</TabsTrigger>
                <TabsTrigger value="history" data-testid="detail-tab-history">History</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-6 space-y-6">
                <SectionPanel title="Personal information" data={appData.personal_info} />
                <ListPanel title="Education" items={appData.education} fields={["institution", "degree", "year", "hours"]} />
                <ListPanel title="Experience" items={appData.experience} fields={["employer", "role", "years", "description"]} />
                <DeclarationPanel d={appData.declarations} />
              </TabsContent>
              <TabsContent value="docs" className="mt-6">
                <DocumentsPanel isOwner={isOwner} appId={appData.id} docs={docs} reload={reload} />
              </TabsContent>
              <TabsContent value="history" className="mt-6">
                <HistoryPanel history={appData.history || []} />
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        <div className="space-y-6">
          <PaymentPanel app={appData} isOwner={isOwner} isAdmin={user.role === "admin"} onChange={reload} />
          {isStaff && <ReviewPanel app={appData} onChange={reload} />}
          {user.role === "admin" && appData.status === "approved" && !appData.credential_id && (
            <IssueCredentialPanel app={appData} onChange={reload} />
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    submitted: "bg-blue-50 text-blue-800 border-blue-200",
    under_review: "bg-amber-50 text-amber-800 border-amber-200",
    info_requested: "bg-orange-50 text-orange-800 border-orange-200",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    denied: "bg-red-50 text-red-800 border-red-200",
  };
  return <Badge variant="outline" className={`capitalize ${map[status]}`}>{(status || "").replace("_", " ")}</Badge>;
}

function SectionPanel({ title, data }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">{title}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {Object.entries(data || {}).map(([k, v]) => (
          <div key={k} className="text-sm">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{k.replace(/_/g, " ")}</div>
            <div className="text-slate-800 mt-0.5">{String(v || "—")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ListPanel({ title, items, fields }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">{title}</div>
      {(!items || items.length === 0) && <div className="text-sm text-slate-500">None provided.</div>}
      <div className="space-y-3">
        {(items || []).map((it, i) => (
          <Card key={i} className="p-4 border-slate-200">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              {fields.map((f) => (
                <div key={f}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{f}</div>
                  <div className="text-slate-800 mt-0.5">{String(it[f] || "—")}</div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function DeclarationPanel({ d }) {
  if (!d) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Declarations</div>
      <ul className="space-y-2 text-sm">
        <li className="flex items-center gap-2">{d.truth ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />} Attestation of truthfulness</li>
        <li className="flex items-center gap-2">{d.ethics ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />} Acceptance of code of ethics</li>
        <li className="flex items-start gap-2">
          {d.disciplinary ? <XCircle className="h-4 w-4 text-red-600 mt-0.5" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />}
          <span>{d.disciplinary ? `Prior disciplinary action disclosed: ${d.disciplinary_note || "no note"}` : "No prior disciplinary action"}</span>
        </li>
      </ul>
    </div>
  );
}

function DocumentsPanel({ isOwner, appId, docs, reload }) {
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("transcript");
  const inputRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      fd.append("application_id", appId);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      await reload();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      {isOwner && (
        <Card className="p-5 border-slate-200 mb-5 bg-slate-50">
          <div className="grid sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-4">
              <Label className="mb-1.5 block text-xs">Document type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-6">
              <Label className="mb-1.5 block text-xs">File (PDF, JPG, PNG · max 10MB)</Label>
              <Input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={upload} data-testid="doc-file-input" />
            </div>
            <div className="sm:col-span-2">
              <Button variant="outline" disabled className="w-full">
                <Upload className="h-4 w-4 mr-1.5" />
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </div>
          </div>
        </Card>
      )}
      {docs.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center">No documents uploaded yet.</div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-md">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700 text-[10px]">{d.doc_type}</Badge>
                  <div className="text-sm font-medium text-primary truncate">{d.original_filename}</div>
                </div>
                <div className="text-xs text-slate-500 mt-1">{new Date(d.created_at).toLocaleString()} · {(d.size / 1024).toFixed(1)} KB</div>
              </div>
              <Button size="sm" variant="outline" data-testid={`doc-download-${d.id}`} onClick={() => downloadFile(d)}>Download</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

async function downloadFile(d) {
  try {
    const resp = await api.get(`/files/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = d.original_filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error(errMessage(e));
  }
}

function HistoryPanel({ history }) {
  if (history.length === 0) return <div className="text-sm text-slate-500">No history yet.</div>;
  return (
    <ol className="relative border-l border-slate-200 pl-6 space-y-5">
      {history.slice().reverse().map((h, i) => (
        <li key={i} className="relative">
          <div className="absolute -left-[33px] top-1 h-3 w-3 rounded-full bg-primary border-2 border-white shadow-sm" />
          <div className="text-xs text-slate-500">{new Date(h.at).toLocaleString()}</div>
          <div className="text-sm font-medium text-primary capitalize">{h.status.replace("_", " ")} · {h.by}</div>
          {h.notes && <div className="text-xs text-slate-600 mt-1 italic">"{h.notes}"</div>}
        </li>
      ))}
    </ol>
  );
}

function PaymentPanel({ app, isOwner, isAdmin, onChange }) {
  const [txn, setTxn] = useState("");
  const [amount, setAmount] = useState(app.payment?.amount || 295);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [squareBusy, setSquareBusy] = useState(false);

  const payWithSquare = async () => {
    setSquareBusy(true);
    try {
      const { data } = await api.post("/payments/create-link", {
        payment_type: "ibthm_application_fee",
        entity_type: "application",
        entity_id: app.id,
        amount: app.payment?.amount || 295,
        currency: "USD",
        redirect_path: `/dashboard/applications/${app.id}`,
      });
      if (data.square_checkout_url) {
        toast.success("Redirecting to Square checkout…");
        window.location.href = data.square_checkout_url;
      } else {
        toast.info(data.fallback_message || "Square checkout link not available — use manual entry below.");
      }
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSquareBusy(false);
    }
  };

  const submit = async () => {
    if (!txn) return;
    setBusy(true);
    try {
      await api.post(`/applications/${app.id}/payment`, { transaction_id: txn, amount: Number(amount), note });
      toast.success("Payment recorded. Awaiting admin verification.");
      setTxn(""); setNote("");
      onChange();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (ok) => {
    setBusy(true);
    try {
      await api.post(`/applications/${app.id}/payment/verify`, { verified: ok, note });
      toast.success(ok ? "Payment verified" : "Payment rejected");
      setNote("");
      onChange();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const p = app.payment || {};

  return (
    <Card className="p-6 border-slate-200" data-testid="payment-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-primary">
          <CreditCard className="h-4 w-4" />
          <h3 className="font-heading font-semibold">Payment</h3>
        </div>
        <PaymentBadge status={p.status} />
      </div>
      <div className="text-sm space-y-2 mb-4">
        <Row label="Processor" value="Square" />
        <Row label="Amount due" value={`$${(p.amount || 0).toFixed(2)}`} />
        <Row label="Transaction ID" value={p.transaction_id || "—"} mono />
        {p.paid_at && <Row label="Submitted" value={new Date(p.paid_at).toLocaleString()} />}
        {p.verified_at && <Row label="Verified" value={`${new Date(p.verified_at).toLocaleString()} · ${p.verified_by || ""}`} />}
      </div>

      {isOwner && p.status !== "verified" && p.status !== "completed" && p.status !== "manually_verified" && !p.consumed && (
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <Button
            data-testid="payment-square-btn"
            disabled={squareBusy}
            onClick={payWithSquare}
            className="w-full bg-[#006AFF] text-white hover:bg-[#0056cc] h-11"
          >
            {squareBusy ? "Creating Square link…" : "Pay with Square"}
          </Button>
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative text-center"><span className="bg-white px-2 text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">or enter manually</span></div>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Square transaction ID</Label>
            <Input data-testid="payment-txn-input" value={txn} onChange={(e) => setTxn(e.target.value)} placeholder="sq_..." />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Amount paid (USD)</Label>
            <Input data-testid="payment-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <Button data-testid="payment-submit-btn" disabled={!txn || busy} className="w-full bg-primary text-white hover:bg-[#113A64]" onClick={submit}>
            {busy ? "Submitting…" : "Submit payment for verification"}
          </Button>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Click "Pay with Square" to use the hosted checkout (real Square account required), or
            enter your transaction ID manually for admin verification.
          </p>
        </div>
      )}

      {isAdmin && p.status === "pending_verification" && (
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <Label className="text-xs">Admin verification note</Label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="payment-admin-note" />
          <div className="flex gap-2">
            <Button onClick={() => verify(true)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payment-verify-btn">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Verify
            </Button>
            <Button onClick={() => verify(false)} variant="outline" className="flex-1 text-red-600 border-red-200" data-testid="payment-reject-btn">
              <XCircle className="h-4 w-4 mr-1.5" /> Reject
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function PaymentBadge({ status }) {
  const map = {
    unpaid: "bg-slate-100 text-slate-700 border-slate-200",
    pending_verification: "bg-amber-50 text-amber-800 border-amber-200",
    verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
    refunded: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <Badge variant="outline" className={`capitalize ${map[status || "unpaid"]}`}>{(status || "unpaid").replace("_", " ")}</Badge>;
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm font-medium ${mono ? "font-mono-data text-primary" : "text-slate-800"}`}>{value}</span>
    </div>
  );
}

function ReviewPanel({ app, onChange }) {
  const [status, setStatus] = useState(app.status);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/applications/${app.id}/status`, { status, reviewer_notes: notes });
      toast.success("Status updated");
      setNotes("");
      onChange();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="review-panel">
      <div className="flex items-center gap-2 text-primary mb-4">
        <ShieldCheck className="h-4 w-4" />
        <h3 className="font-heading font-semibold">Reviewer actions</h3>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="mb-1.5 block text-xs">New status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="review-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_review">Under review</SelectItem>
              <SelectItem value="info_requested">Information requested</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="denied">Denied</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Reviewer notes</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="review-notes" />
        </div>
        <Button onClick={submit} disabled={busy} className="w-full bg-primary text-white hover:bg-[#113A64]" data-testid="review-submit-btn">
          {busy ? "Saving…" : "Save status"}
        </Button>
      </div>
    </Card>
  );
}

function IssueCredentialPanel({ app, onChange }) {
  const [state, setState] = useState("");
  const [years, setYears] = useState(2);
  const [busy, setBusy] = useState(false);

  const issue = async () => {
    setBusy(true);
    try {
      await api.post("/credentials/issue", {
        application_id: app.id,
        credential_type: app.credential_type,
        state,
        valid_years: Number(years),
      });
      toast.success("Credential issued");
      onChange();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 border-slate-200 bg-emerald-50/50" data-testid="issue-panel">
      <div className="flex items-center gap-2 text-emerald-800 mb-3">
        <Award className="h-4 w-4" />
        <h3 className="font-heading font-semibold">Issue credential</h3>
      </div>
      <p className="text-xs text-slate-600 mb-4">This application is approved. Issue the credential to add it to the public registry.</p>
      <div className="space-y-3">
        <div>
          <Label className="mb-1.5 block text-xs">State / jurisdiction (optional)</Label>
          <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="e.g. NY" data-testid="issue-state-input" />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Validity (years)</Label>
          <Input type="number" value={years} onChange={(e) => setYears(e.target.value)} data-testid="issue-years-input" />
        </div>
        <Button onClick={issue} disabled={busy} className="w-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="issue-submit-btn">
          {busy ? "Issuing…" : "Issue credential"}
        </Button>
      </div>
    </Card>
  );
}
